import api from './api';
import type {
  SurveyTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  SurveyDispatch,
  CreateDispatchRequest,
  SurveyInvitation,
  SurveyResponse,
  DispatchFilters,
  PaginatedResponse,
  PublicSurveyData,
  SubmitSurveyRequest,
} from '../types';

export const surveyService = {
  // Templates
  async getTemplates(): Promise<SurveyTemplate[]> {
    const response = await api.get('/surveys/templates');
    const raw = response.data;
    // API returns { data: [...], pagination: {...} }
    if (raw && Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
  },

  async getTemplateById(id: string): Promise<SurveyTemplate> {
    const response = await api.get<SurveyTemplate>(`/surveys/templates/${id}`);
    return response.data;
  },

  async createTemplate(data: CreateTemplateRequest): Promise<SurveyTemplate> {
    const response = await api.post<SurveyTemplate>('/surveys/templates', data);
    return response.data;
  },

  async updateTemplate(id: string, data: UpdateTemplateRequest): Promise<SurveyTemplate> {
    const response = await api.put<SurveyTemplate>(`/surveys/templates/${id}`, data);
    return response.data;
  },

  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/surveys/templates/${id}`);
  },

  // Dispatches
  async getDispatches(params?: DispatchFilters): Promise<PaginatedResponse<SurveyDispatch>> {
    const response = await api.get('/surveys/dispatches', { params });
    const raw = response.data;
    // API returns { data: [...], pagination: { page, limit, total, totalPages } }
    if (raw && raw.pagination) {
      return {
        data: raw.data ?? [],
        page: raw.pagination.page ?? 1,
        pageSize: raw.pagination.limit ?? 100,
        total: raw.pagination.total ?? 0,
        totalPages: raw.pagination.totalPages ?? 1,
      };
    }
    if (Array.isArray(raw)) {
      return {
        data: raw,
        page: 1,
        pageSize: raw.length,
        total: raw.length,
        totalPages: 1,
      };
    }
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? raw?.limit ?? 100,
      total: raw?.total ?? 0,
      totalPages: raw?.totalPages ?? 1,
    };
  },

  async getDispatchById(id: string): Promise<SurveyDispatch> {
    const response = await api.get<SurveyDispatch>(`/surveys/dispatches/${id}`);
    return response.data;
  },

  async createDispatch(data: CreateDispatchRequest): Promise<SurveyDispatch> {
    const response = await api.post<SurveyDispatch>('/surveys/dispatches', data);
    return response.data;
  },

  async cancelDispatch(id: string): Promise<void> {
    await api.post(`/surveys/dispatches/${id}/cancel`);
  },

  async sendDispatch(id: string): Promise<any> {
    const response = await api.post(`/surveys/dispatches/${id}/send`);
    return response.data;
  },

  async getDispatchInvitations(dispatchId: string): Promise<SurveyInvitation[]> {
    const response = await api.get(`/surveys/dispatches/${dispatchId}/invitations`);
    const raw = response.data;
    // Could be array or { data: [...] } or embedded in dispatch response
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.data)) return raw.data;
    return [];
  },

  async resendInvitation(invitationId: string): Promise<void> {
    await api.post(`/surveys/invitations/${invitationId}/resend`);
  },

  // Responses
  async getResponses(dispatchId: string): Promise<SurveyResponse[]> {
    const response = await api.get(`/surveys/dispatches/${dispatchId}/responses`);
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.data)) return raw.data;
    return [];
  },

  // Public survey endpoints (no auth)
  async getPublicSurvey(token: string): Promise<PublicSurveyData> {
    const response = await api.get<PublicSurveyData>(`/surveys/public/${token}`);
    return response.data;
  },

  async submitSurvey(token: string, data: SubmitSurveyRequest): Promise<void> {
    await api.post(`/surveys/public/${token}`, data);
  },
};
